import { z } from "zod";
import { exportStaticPdf } from "../pdf-export";
import { pdfTextSupportError } from "../pdf-text-support";
import { assertPdfTextFits } from "../pdf-text-fit";
import {
  PDF_FORM_FIELD_TYPE,
  PDF_FORM_FIELD_FLAG,
  PdfAnnotationSubtype,
  PdfStandardFont,
  PdfTextAlignment,
  PdfVerticalAlignment,
} from "@embedpdf/models";
import type {
  PdfAnnotationObject,
  PdfWidgetAnnoObject,
  PdfDocumentObject,
  PdfEngine,
} from "@embedpdf/models";
import type { AnnotationScope } from "@embedpdf/plugin-annotation";
import type { FormScope } from "@embedpdf/plugin-form";

const PDF_TEXT_FONTS = {
  Helvetica: PdfStandardFont.Helvetica,
  "Helvetica-Bold": PdfStandardFont.Helvetica_Bold,
  "Helvetica-Oblique": PdfStandardFont.Helvetica_Oblique,
  "Times-Roman": PdfStandardFont.Times_Roman,
  "Times-Bold": PdfStandardFont.Times_Bold,
  "Times-Italic": PdfStandardFont.Times_Italic,
  Courier: PdfStandardFont.Courier,
  "Courier-Bold": PdfStandardFont.Courier_Bold,
} as const;
const fontFamily = z.enum(Object.keys(PDF_TEXT_FONTS) as [keyof typeof PDF_TEXT_FONTS, ...Array<keyof typeof PDF_TEXT_FONTS>]);
const textChanges = {
  x: z.number().nonnegative().max(10000).optional(),
  y: z.number().nonnegative().max(10000).optional(),
  width: z.number().positive().max(10000).optional(),
  height: z.number().min(8).max(10000).optional(),
  text: z.string().min(1).max(10000).optional(),
  fontSize: z.number().min(6).max(96).optional(),
  fontFamily: fontFamily.optional(),
};
const base = { documentId: z.string().min(1).max(128) };
const revision = { expectedRevision: z.number().int().nonnegative() };
const pageIndex = z.number().int().nonnegative();
export const PDF_REMOTE_SCHEMAS = {
  fillapp_pdf_read_state: z
    .object({
      documentId: base.documentId.optional(),
      pageIndex: pageIndex.optional(),
    })
    .strict(),
  fillapp_pdf_set_fields: z
    .object({
      ...base,
      ...revision,
      values: z
        .record(z.string().min(1).max(256), z.string().max(10000))
        .refine(
          (v) => Object.keys(v).length > 0 && Object.keys(v).length <= 100,
        ),
    })
    .strict(),
  fillapp_pdf_add_text: z
    .object({
      ...base,
      ...revision,
      pageIndex,
      x: z.number().nonnegative(),
      y: z.number().nonnegative(),
      width: z.number().positive(),
      height: z.number().positive(),
      text: z.string().min(1).max(10000),
      fontSize: z.number().min(6).max(96).optional(),
      fontFamily: fontFamily.optional(),
    })
    .strict(),
  fillapp_pdf_update_text: z
    .object({ ...base, ...revision, annotationId: z.string().min(1).max(128), ...textChanges })
    .strict()
    .refine((args) => Object.keys(textChanges).some((key) => args[key as keyof typeof args] !== undefined), "Supply at least one text change."),
  fillapp_pdf_remove_annotation: z
    .object({ ...base, ...revision, annotationId: z.string().min(1).max(128) })
    .strict(),
  fillapp_pdf_render_page: z
    .object({
      ...base,
      pageIndex,
      maxWidth: z.number().int().min(256).max(1600).optional(),
    })
    .strict(),
  fillapp_pdf_export: z.object({ ...base, ...revision, format: z.enum(["editable", "static"]).optional() }).strict(),
};
export interface RemotePdfCommand {
  name: string;
  expiresAt?: number;
  arguments: Record<string, unknown>;
}
export interface RemotePdfRuntime {
  documentId: string;
  fileName: string;
  revision: () => number;
  engine: PdfEngine<Blob>;
  doc: PdfDocumentObject;
  form: FormScope;
  annotation: AnnotationScope;
  persist: () => Promise<void>;
  save: () => Promise<Uint8Array>;
  editorUrl?: string;
  hasXfa?: boolean;
}
function isWidget(object: PdfAnnotationObject): object is PdfWidgetAnnoObject {
  return object.type === PdfAnnotationSubtype.WIDGET;
}

/** Keeps original PDF keys for writes, while exposing meaningful labels and native values. */
export function describePdfFields(runtime: RemotePdfRuntime) {
  const widgets = runtime.annotation
    .getAnnotations()
    .map((entry) => entry.object)
    .filter(isWidget);
  return runtime.form.getFormFields().map((field, index) => {
    const matches = widgets.filter(
      (widget) => widget.field.name === field.name,
    );
    const alternateName = matches
      .find((widget) => widget.field.alternateName?.trim())
      ?.field.alternateName.trim()
      .slice(0, 256);
    const technicalName = /\[\d+\]|topmostSubform|^f\d+_\d+$|^c\d+_\d+$/i.test(
      field.name,
    );
    const label =
      alternateName ||
      (technicalName ? `Field ${index + 1}` : field.name.slice(0, 256));
    const checkedValue =
      [PDF_FORM_FIELD_TYPE.CHECKBOX, PDF_FORM_FIELD_TYPE.RADIOBUTTON].includes(field.type)
      ? matches.find((widget) => widget.exportValue)?.exportValue ||
      (field.value && field.value !== "Off" ? field.value : undefined)
      : undefined;
    const choices =
      field.type === PDF_FORM_FIELD_TYPE.RADIOBUTTON
        ? [
            ...new Map(
              matches
                .filter((widget) => widget.exportValue)
                .map((widget, optionIndex) => [
                  widget.exportValue!,
                  {
                    value: widget.exportValue!,
                    label:
                      widget.field.alternateName?.trim().slice(0, 256) ||
                      `Option ${optionIndex + 1}`,
                  },
                ]),
            ).values(),
          ]
        : (field.options || []).map((option) => ({
            value: option.label,
            label: option.label,
          }));
    const duplicatedLabels = new Set(
      choices
        .filter(
          (choice) =>
            choices.filter((other) => other.label === choice.label).length > 1,
        )
        .map((choice) => choice.label),
    );
    for (const [choiceIndex, choice] of choices.entries()) {
      if (duplicatedLabels.has(choice.label))
        choice.label = /[a-z]/i.test(choice.value) && !/\[\d+\]|topmostSubform/i.test(choice.value)
          ? choice.value.slice(0, 256)
          : `Option ${choiceIndex + 1}`;
    }
    const selectedValue =
      field.type === PDF_FORM_FIELD_TYPE.RADIOBUTTON
        ? field.value
        : field.options?.find((option) => option.isSelected)?.label ??
          (choices.some((choice) => choice.value === field.value) ||
          !field.value
            ? field.value
            : undefined);
    const multiSelect = matches.some(
      (widget) =>
        !!(widget.field.flag & PDF_FORM_FIELD_FLAG.CHOICE_MULTL_SELECT),
    );
    const multiline = matches.some((widget) => !!(widget.field.flag & PDF_FORM_FIELD_FLAG.TEXT_MULTIPLINE));
    const maxLength = matches.map((widget) => widget.field.type === PDF_FORM_FIELD_TYPE.TEXTFIELD ? widget.field.maxLen : undefined).filter((length): length is number => typeof length === "number" && length > 0).sort((a, b) => a - b)[0];
    const controlType = multiSelect
      ? "unsupported"
      : field.type === PDF_FORM_FIELD_TYPE.TEXTFIELD
        ? "text"
        : field.type === PDF_FORM_FIELD_TYPE.CHECKBOX && checkedValue
          ? "checkbox"
          : [
                PDF_FORM_FIELD_TYPE.RADIOBUTTON,
                PDF_FORM_FIELD_TYPE.COMBOBOX,
                PDF_FORM_FIELD_TYPE.LISTBOX,
              ].includes(field.type) &&
              choices.length &&
              selectedValue !== undefined
            ? "choice"
            : "unsupported";
    return {
      ...field,
      label,
      controlType,
      ...(field.type === PDF_FORM_FIELD_TYPE.TEXTFIELD ? { multiline, ...(maxLength ? { maxLength } : {}) } : {}),
      ...(selectedValue !== undefined ? { selectedValue } : {}),
      ...(checkedValue ? { checkedValue } : {}),
      ...(choices.length ? { choices } : {}),
      pageIndexes: [...new Set(matches.map((widget) => widget.pageIndex))],
      widgets: matches.map((widget) => ({ pageIndex: widget.pageIndex, rect: widget.rect })),
    };
  });
}

async function blobBase64(blob: Blob): Promise<string> {
  if (blob.size > 5 * 1024 * 1024)
    throw new Error(
      "Result exceeds the 5 MB relay limit. Download it directly in FillApp.",
    );
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 8192)
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  return btoa(binary);
}

/** Executes only the explicitly shared document; never invokes an AI provider. */
export async function executePdfCommand(
  runtime: RemotePdfRuntime,
  command: RemotePdfCommand,
): Promise<Record<string, unknown>> {
  if (
    command.expiresAt !== undefined &&
    (!Number.isFinite(command.expiresAt) || command.expiresAt <= Date.now())
  )
    throw new Error("Command expired. Request a fresh operation.");
  const schema =
    PDF_REMOTE_SCHEMAS[command.name as keyof typeof PDF_REMOTE_SCHEMAS];
  if (!schema) throw new Error("Unsupported PDF tool.");
  const args = schema.parse(command.arguments);
  if (args.documentId !== undefined && args.documentId !== runtime.documentId)
    throw new Error("This document has not been shared with the connection.");
  const initialRevision = runtime.revision();
  let createdAnnotationId: string | undefined;
  if ("expectedRevision" in args && args.expectedRevision !== initialRevision)
    throw new Error(
      "Document changed. Read its current state before editing or exporting.",
    );
  const page =
    "pageIndex" in args && args.pageIndex !== undefined
      ? runtime.doc.pages[args.pageIndex]
      : undefined;
  if ("pageIndex" in args && args.pageIndex !== undefined && !page)
    throw new Error("Page does not exist.");
  if (command.name === "fillapp_pdf_read_state") {
    const text = page
      ? (
          await runtime.engine
            .extractText(runtime.doc, [page.index])
            .toPromise()
        ).slice(0, 50000)
      : undefined;
    if (runtime.revision() !== initialRevision)
      throw new Error("Document changed while reading. Read again.");
    return {
      documentId: runtime.documentId,
      fileName: runtime.fileName,
      revision: initialRevision,
      ...(runtime.hasXfa ? { hasXfa: true } : {}),
      ...(runtime.editorUrl ? { editorUrl: runtime.editorUrl } : {}),
      pages: runtime.doc.pages.map((p) => ({
        pageIndex: p.index,
        width: p.size.width,
        height: p.size.height,
      })),
      ...(runtime.hasXfa ? { exportNotice: "This XFA form retains dynamic data. Use static export to preserve visible entries across viewers." } : {}),
      fields: describePdfFields(runtime).filter(
        (field) => !page || field.pageIndexes.includes(page.index),
      ),
      annotations: runtime.annotation
        .getAnnotations()
        .filter((a) => a.object.type !== PdfAnnotationSubtype.WIDGET &&
          (!page || a.object.pageIndex === page.index))
        .map((a) => ({
          id: a.object.id,
          pageIndex: a.object.pageIndex,
          type: a.object.type,
          rect: a.object.rect,
          contents: a.object.contents,
          ...(a.object.type === PdfAnnotationSubtype.FREETEXT ? {
            fontSize: a.object.fontSize,
            fontFamily: Object.entries(PDF_TEXT_FONTS).find(([, value]) => value === (a.object as Extract<PdfAnnotationObject, { type: PdfAnnotationSubtype.FREETEXT }>).fontFamily)?.[0],
          } : {}),
        })),
      ...(text !== undefined ? { text } : {}),
    };
  }
  if (command.name === "fillapp_pdf_set_fields") {
    const args = PDF_REMOTE_SCHEMAS.fillapp_pdf_set_fields.parse(
      command.arguments,
    );
    const fields = describePdfFields(runtime);
    for (const name of Object.keys(args.values)) {
      if (["__proto__", "prototype", "constructor"].includes(name))
        throw new Error("Unsupported field name.");
      const field = fields.find((f) => f.name === name);
      if (!field || field.readOnly || field.controlType === "unsupported")
        throw new Error(`Unknown or read-only field: ${name}`);
      if (field.controlType === "text") {
        const textError = pdfTextSupportError(args.values[name]);
        if (textError) throw new Error(textError);
      }
      if (field.controlType === "text" && !field.multiline && /[\r\n]/.test(args.values[name]))
        throw new Error(`This field accepts one line: ${field.label}`);
      if (field.maxLength && args.values[name].length > field.maxLength)
        throw new Error(`Field exceeds its ${field.maxLength} character limit: ${field.label}`);
      if (
        field.choices?.length &&
        !field.choices.some((option) => option.value === args.values[name]) &&
        !(
          field.type === PDF_FORM_FIELD_TYPE.RADIOBUTTON &&
          args.values[name] === "Off"
        )
      )
        throw new Error(`Invalid option for field: ${name}`);
      if (
        field.controlType === "checkbox" &&
        !["Off", field.checkedValue].includes(args.values[name])
      )
        throw new Error(`Invalid checkbox value for field: ${name}`);
    }
    const success = await runtime.form.setFormValues(args.values).toPromise();
    if (!success) throw new Error("PDF rejected the field changes.");
  } else if (command.name === "fillapp_pdf_add_text" && page) {
    const args = PDF_REMOTE_SCHEMAS.fillapp_pdf_add_text.parse(
      command.arguments,
    );
    const textError = pdfTextSupportError(args.text);
    if (textError) throw new Error(textError);
    if (
      args.x + args.width > page.size.width ||
      args.y + args.height > page.size.height
    )
      throw new Error("Text rectangle must fit inside the page.");
    if (args.height < 8) throw new Error("Text box is too short. Use a height of at least 8 points.");
    if (args.fontSize !== undefined && args.fontSize > args.height * 0.85)
      throw new Error("Text box is too short for the requested font size. No text was added. Increase its height to preserve the font size.");
    const size = args.fontSize ?? Math.max(6, Math.min(12, Math.floor(args.height * 0.85)));
    assertPdfTextFits(args.text, args.width, args.height, size, args.fontFamily ?? "Helvetica");
    createdAnnotationId = crypto.randomUUID();
    runtime.annotation.createAnnotation(page.index, {
      id: createdAnnotationId,
      pageIndex: page.index,
      type: PdfAnnotationSubtype.FREETEXT,
      rect: {
        origin: { x: args.x, y: args.y },
        size: { width: args.width, height: args.height },
      },
      contents: args.text,
      fontFamily: PDF_TEXT_FONTS[args.fontFamily ?? "Helvetica"],
      fontSize: size,
      fontColor: "#000000",
      textAlign: PdfTextAlignment.Left,
      verticalAlign: PdfVerticalAlignment.Top,
      opacity: 1,
    });
  } else if (command.name === "fillapp_pdf_update_text") {
    const args = PDF_REMOTE_SCHEMAS.fillapp_pdf_update_text.parse(command.arguments);
    const annotation = runtime.annotation.getAnnotations().find((entry) => entry.object.id === args.annotationId);
    if (!annotation || annotation.object.type !== PdfAnnotationSubtype.FREETEXT)
      throw new Error("Editable text annotation does not exist.");
    const current = annotation.object;
    const textError = pdfTextSupportError(args.text ?? current.contents);
    if (textError) throw new Error(textError);
    const targetPage = runtime.doc.pages[current.pageIndex];
    if (!targetPage) throw new Error("Page does not exist.");
    const rect = {
      origin: { x: args.x ?? current.rect.origin.x, y: args.y ?? current.rect.origin.y },
      size: { width: args.width ?? current.rect.size.width, height: args.height ?? current.rect.size.height },
    };
    if (rect.origin.x + rect.size.width > targetPage.size.width || rect.origin.y + rect.size.height > targetPage.size.height)
      throw new Error("Text rectangle must fit inside the page.");
    const size = args.fontSize ?? current.fontSize;
    if (rect.size.height < 8 || size > rect.size.height * 0.85)
      throw new Error("Text box is too short for this font size. Increase its height.");
    const font = args.fontFamily ?? Object.entries(PDF_TEXT_FONTS).find(([, value]) => value === current.fontFamily)?.[0] as keyof typeof PDF_TEXT_FONTS | undefined;
    assertPdfTextFits(args.text ?? current.contents, rect.size.width, rect.size.height, size, font ?? "Helvetica");
    runtime.annotation.updateAnnotation(current.pageIndex, current.id, {
      rect,
      contents: args.text ?? current.contents,
      fontSize: size,
      fontFamily: args.fontFamily ? PDF_TEXT_FONTS[args.fontFamily] : current.fontFamily,
    });
  } else if (command.name === "fillapp_pdf_remove_annotation") {
    const args = PDF_REMOTE_SCHEMAS.fillapp_pdf_remove_annotation.parse(
      command.arguments,
    );
    const annotation = runtime.annotation
      .getAnnotations()
      .find(
        (a) =>
          a.object.id === args.annotationId &&
          a.object.type !== PdfAnnotationSubtype.WIDGET,
      );
    if (!annotation) throw new Error("Editable annotation does not exist.");
    runtime.annotation.deleteAnnotation(
      annotation.object.pageIndex,
      annotation.object.id,
    );
  } else if (command.name === "fillapp_pdf_render_page" && page) {
    const args = PDF_REMOTE_SCHEMAS.fillapp_pdf_render_page.parse(
      command.arguments,
    );
    const width = args.maxWidth ?? 1200;
    const scale = Math.min(width / page.size.width, 1600 / page.size.height, 2);
    const blob = await runtime.engine
      .renderPage(runtime.doc, page, {
        scaleFactor: scale,
        dpr: 1,
        withAnnotations: true,
        withForms: true,
        imageType: "image/png",
      })
      .toPromise();
    const screenshotBase64 = await blobBase64(blob);
    if (runtime.revision() !== initialRevision)
      throw new Error("Document changed while rendering. Read again.");
    return {
      documentId: runtime.documentId,
      revision: initialRevision,
      screenshotBase64,
      screenshotMediaType: "image/png",
    };
  } else if (command.name === "fillapp_pdf_export") {
    const args = PDF_REMOTE_SCHEMAS.fillapp_pdf_export.parse(command.arguments);
    const exportFormat = args.format ?? "static";
    let bytes: Uint8Array;
    if (exportFormat === "static") {
      await runtime.annotation.commit().toPromise();
      bytes = await exportStaticPdf(runtime.engine, runtime.doc);
    } else bytes = await runtime.save();
    const fileBase64 = await blobBase64(
      new Blob([bytes as BlobPart], { type: "application/pdf" }),
    );
    if (runtime.revision() !== initialRevision)
      throw new Error("Document changed while exporting. Read again.");
    return {
      documentId: runtime.documentId,
      revision: initialRevision,
      fileBase64,
      mimeType: "application/pdf",
      filename: runtime.fileName.replace(/\.pdf$/i, "") + (exportFormat === "static" ? "-static.pdf" : "-filled.pdf"),
      exportFormat,
      notice: exportFormat === "static" ? "Fixed copy. Continue editing in FillApp." : "Editable copies may retain original dynamic form data. Use format static for a fixed copy.",
    };
  } else throw new Error("Unsupported PDF arguments.");
  await runtime.persist();
  return {
    documentId: runtime.documentId,
    revision: runtime.revision(),
    changed: true,
    ...(createdAnnotationId ? { annotationId: createdAnnotationId } : {}),
  };
}
